import type { i18n, Resource } from 'i18next'
import { createInstance } from 'i18next'
import resourcesToBackend from 'i18next-resources-to-backend'
import { initReactI18next } from 'react-i18next/initReactI18next'

import i18nConfig from './i18nConfig'

const aliasMap: Record<string, string> = {
  'talent-pool': 'talent_pool',
  'assign-job': 'assignJobs',
  'career-hub': 'careerHub',
  'agency-company-listing': 'companiesListing',
  'agency-company': 'company',
  'agency-contacts': 'contact',
  'agency-setting-company-status': 'companyStatus',
  'audit-logs': 'audit_logs',
  'career-page-editor': 'career_editor',
  'course-management': 'course',
  'setting-company-settings': 'companySettings'
}

export const getFileName = (name: string) => {
  return Object.fromEntries(Object.entries(aliasMap).map(([k, v]) => [v, k]))[name] || name
}

export const defaultI18nSchemas = [
  'common',
  'auth',
  'form',
  'button',
  'tooltip',
  'label',
  'notification',
  'task',
  'activity',
  'onboarding',
  'welcome',
  'candidates',
  'careers',
  'job',
  'interview',
  'settings',
  'placements',
  'referrals',
  'dashboard',
  'requisitions',
  'modal',
  'jobs',
  'report',
  'talent_pool',
  'assignJobs',
  'careerHub',
  'companiesListing',
  'company',
  'contact',
  'companyStatus',
  'audit_logs',
  'career_editor',
  'course',
  'companySettings'
]

export default async function initTranslations(
  locale: string,
  namespaces: string[],
  i18nInstance?: i18n,
  resources?: Resource
) {
  i18nInstance = i18nInstance || createInstance()
  i18nInstance.use(initReactI18next)

  if (!resources) {
    i18nInstance.use(
      resourcesToBackend((language: string, namespace: string) => {
        const fileName = getFileName(namespace)
        return import(`../../../public/locales/${language}/${fileName}.json`)
          .then((module) => module.default || module)
          .catch(() => ({}))
      })
    )
  }

  await i18nInstance.init({
    lng: locale || i18nConfig.defaultLocale,
    resources,
    fallbackLng: i18nConfig.defaultLocale,
    supportedLngs: i18nConfig.locales,
    defaultNS: namespaces[0] || 'common',
    fallbackNS: 'common',
    ns: namespaces,
    preload: resources ? [] : i18nConfig.locales,
    interpolation: { escapeValue: false },
    detection: {
      order: ['cookie', 'navigator'],
      caches: ['cookie'],
      cookieName: i18nConfig.localeCookie,
      cookieOptions: {
        path: '/',
        sameSite: 'lax',
        secure: process.env.NODE_ENV === 'production'
      }
    },
    returnObjects: false,
    returnNull: false,
    returnEmptyString: false
  })

  return {
    i18n: i18nInstance,
    resources: {
      [locale]: i18nInstance.services.resourceStore.data[locale]
    } as Resource,
    t: i18nInstance.t
  }
}
